import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  signOut,
  updateProfile,
  onAuthStateChanged,
  fetchSignInMethodsForEmail,
  MultiFactorError,
  type User,
} from "firebase/auth";

import { auth } from "@/lib/firebase";

type RegisterOptions = {
  email: string;
  password: string;
  displayName?: string;
};

type LoginOptions = {
  email: string;
  password: string;
};

export async function registerWithEmail({
  email,
  password,
  displayName,
}: RegisterOptions): Promise<User> {
  const credential = await createUserWithEmailAndPassword(auth(), email, password);
  if (displayName) {
    await updateProfile(credential.user, { displayName });
  }
  return credential.user;
}

export async function loginWithEmail({ email, password }: LoginOptions): Promise<User> {
  const credential = await signInWithEmailAndPassword(auth(), email, password);
  return credential.user;
}

export async function loginWithGoogle(): Promise<User> {
  const provider = new GoogleAuthProvider();
  // Force account picker so users can choose after signing out
  provider.setCustomParameters({ prompt: "select_account" });
  const credential = await signInWithPopup(auth(), provider);
  return credential.user;
}

export async function resetPassword(email: string): Promise<void> {
  // Configure action code settings for password reset
  const actionCodeSettings = {
    url: `${window.location.origin}/reset-password`, // Custom branded password reset page
    handleCodeInApp: false, // Firebase sends link with query params to our custom page
  };
  
  // Security note: Firebase will silently succeed even if email doesn't exist
  // This prevents email enumeration attacks where attackers test which emails have accounts
  await sendPasswordResetEmail(auth(), email, actionCodeSettings);
}

export async function logout(): Promise<void> {
  await signOut(auth());
}

export function observeAuth(callback: (user: User | null) => void): () => void {
  return onAuthStateChanged(auth(), callback);
}
